import { SelectableShift, TipData, TipUser } from '../types';

/**
 * Transform selected shifts into TipData format for the distribution table.
 * Each shift becomes a "day" (column) in the table.
 */
export function transformShiftsToTipData(shifts: SelectableShift[]): TipData {
  if (shifts.length === 0) {
    return {
      start_date: '',
      end_date: '',
      total_tips: 0,
      total_distributed: 0,
      total_employees: 0,
      days: {},
    };
  }

  // Sort shifts by start_time
  const sortedShifts = [...shifts].sort(
    (a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime()
  );

  // Collect all unique users across all shifts
  const allUsersMap = new Map<number, { user_id: number; user_name: string }>();
  
  for (const shift of sortedShifts) {
    for (const [userId, userStats] of Object.entries(shift.users_stats)) {
      const id = parseInt(userId, 10);
      if (!allUsersMap.has(id)) {
        allUsersMap.set(id, {
          user_id: id,
          user_name: userStats.name || userStats.user_name || 'Unknown',
        });
      }
    }
  }

  const allUsers = Array.from(allUsersMap.values());
  const totalTips = sortedShifts.reduce((sum, s) => sum + s.total_tip, 0);

  // Create days object - each shift becomes a "day"
  const days: TipData['days'] = {};

  for (const shift of sortedShifts) {
    // Use shift_id as the key to ensure uniqueness
    const dateKey = shift.start_time.split(' ')[0];
    
    // Get users who participated in this shift
    const shiftUserIds = new Set(Object.keys(shift.users_stats).map(Number));

    // Create TipUser entries for all users
    const users: TipUser[] = allUsers.map((user) => {
      const participated = shiftUserIds.has(user.user_id);
      return {
        user_id: user.user_id,
        user_name: user.user_name,
        rate: participated ? 100 : 0, // Default rate: 100 if participated, 0 if not
        attended: {
          [dateKey]: participated,
        },
      };
    });

    days[dateKey] = {
      value: shift.total_tip,
      users,
    };
  }

  // Calculate date range
  const startDate = sortedShifts[0].start_time.split(' ')[0];
  const endDate = sortedShifts[sortedShifts.length - 1].end_time.split(' ')[0];

  return {
    start_date: startDate,
    end_date: endDate,
    total_tips: totalTips,
    total_distributed: 0, // Will be calculated based on distribution
    total_employees: allUsers.length,
    days,
  };
}

/**
 * Format a shift key (shift_123) into a display label
 */
export function formatShiftLabel(shiftKey: string, shifts: SelectableShift[]): string {
  const shiftId = parseInt(shiftKey.replace('shift_', ''), 10);
  const shift = shifts.find((s) => s.shift_id === shiftId);
  
  if (!shift) return shiftKey;
  
  const date = new Date(shift.start_time);
  const dayName = date.toLocaleDateString('es-CL', { weekday: 'short' });
  const dayNum = date.getDate();
  
  return `${dayName} ${dayNum}`;
}

/**
 * Get full shift info for display
 */
export function getShiftDisplayInfo(shiftKey: string, shifts: SelectableShift[]): {
  name: string;
  date: string;
  time: string;
} {
  const shiftId = parseInt(shiftKey.replace('shift_', ''), 10);
  const shift = shifts.find((s) => s.shift_id === shiftId);
  
  if (!shift) {
    return { name: 'Unknown', date: '', time: '' };
  }
  
  const startDate = new Date(shift.start_time);
  const endDate = new Date(shift.end_time);
  
  return {
    name: shift.name,
    date: startDate.toLocaleDateString('es-CL', { 
      weekday: 'short', 
      day: 'numeric', 
      month: 'short' 
    }),
    time: `${startDate.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' })} - ${endDate.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' })}`,
  };
}
